import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState
} from 'react';
import type React from 'react';

import { DropdownPlacement } from '../types';

export function useDropdownPosition(
  triggerRef: React.RefObject<HTMLElement>,
  overlayRef: React.RefObject<HTMLElement>,
  isOpen: boolean,
  placement: DropdownPlacement = 'bottom',
  offset = 4
) {
  const [position, setPosition] = useState<React.CSSProperties>({});

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !isOpen) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();

    const layoutHeight = window.innerHeight;
    const actualVisualViewport = window.visualViewport;
    const visualWidth = actualVisualViewport
      ? actualVisualViewport.width
      : window.innerWidth;
    const visualHeight = actualVisualViewport
      ? actualVisualViewport.height
      : layoutHeight;
    const visualOffsetTop = actualVisualViewport
      ? actualVisualViewport.offsetTop
      : 0;
    const visualOffsetLeft = actualVisualViewport
      ? actualVisualViewport.offsetLeft
      : 0;

    const overlayRect = overlayRef.current?.getBoundingClientRect();
    const overlayHeight =
      overlayRect?.height || overlayRef.current?.offsetHeight || 320;
    const overlayWidth =
      overlayRect?.width ||
      overlayRef.current?.offsetWidth ||
      triggerRect.width;

    let top: number | undefined;
    let bottom: number | undefined;
    let left: number;

    const spaceBelow = visualOffsetTop + visualHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top - visualOffsetTop;

    let actualPlacement = placement;
    if (placement === 'auto') {
      actualPlacement =
        spaceBelow >= overlayHeight || spaceBelow >= spaceAbove
          ? 'bottom'
          : 'top';
    } else if (
      placement.startsWith('bottom') &&
      spaceBelow < overlayHeight &&
      spaceAbove > spaceBelow
    ) {
      actualPlacement = placement.replace('bottom', 'top') as DropdownPlacement;
    } else if (
      placement.startsWith('top') &&
      spaceAbove < overlayHeight &&
      spaceBelow > spaceAbove
    ) {
      actualPlacement = placement.replace('top', 'bottom') as DropdownPlacement;
    }

    if (actualPlacement.startsWith('bottom')) {
      top = triggerRect.bottom + offset;
      bottom = undefined;
    } else if (actualPlacement.startsWith('top')) {
      top = undefined;
      bottom = layoutHeight - triggerRect.top + offset;
    } else if (
      actualPlacement.startsWith('left') ||
      actualPlacement.startsWith('right')
    ) {
      top = triggerRect.top + (triggerRect.height - overlayHeight) / 2;
      bottom = undefined;
    } else {
      top = triggerRect.bottom + offset;
      bottom = undefined;
    }

    if (
      actualPlacement.includes('left') &&
      !actualPlacement.startsWith('left')
    ) {
      left = triggerRect.left;
    } else if (
      actualPlacement.includes('right') &&
      !actualPlacement.startsWith('right')
    ) {
      left = triggerRect.right - overlayWidth;
    } else if (actualPlacement.includes('start')) {
      left = triggerRect.left;
    } else if (actualPlacement.includes('end')) {
      left = triggerRect.right - overlayWidth;
    } else if (actualPlacement.startsWith('left')) {
      left = triggerRect.left - overlayWidth - offset;
    } else if (actualPlacement.startsWith('right')) {
      left = triggerRect.right + offset;
    } else {
      left = triggerRect.left;
    }

    const visualLeftEdge = visualOffsetLeft;
    const visualRightEdge = visualOffsetLeft + visualWidth;
    if (left < visualLeftEdge + 8) left = visualLeftEdge + 8;
    if (left + overlayWidth > visualRightEdge - 8) {
      left = visualRightEdge - overlayWidth - 8;
    }

    if (top !== undefined) {
      const minTop = visualOffsetTop + 8;
      if (top < minTop) top = minTop;
      const maxTop = visualOffsetTop + visualHeight - overlayHeight - 8;
      if (top > maxTop) top = maxTop;
    }
    if (bottom !== undefined) {
      const minBottom = layoutHeight - visualOffsetTop - visualHeight + 8;
      if (bottom < minBottom) bottom = minBottom;
      const maxBottom = layoutHeight - visualOffsetTop - overlayHeight - 8;
      if (bottom > maxBottom) bottom = maxBottom;
    }

    const positionStyles: React.CSSProperties = {
      position: 'fixed',
      left: `${left + 2}px`,
      overflow: 'hidden'
    };

    if (top !== undefined) {
      positionStyles.top = `${top}px`;
    }
    if (bottom !== undefined) {
      positionStyles.bottom = `${bottom}px`;
    }

    setPosition(positionStyles);
  }, [triggerRef, overlayRef, isOpen, placement, offset]);

  useLayoutEffect(() => {
    if (!isOpen) return;

    updatePosition();

    const raf = requestAnimationFrame(() => {
      updatePosition();
    });
    const settleEarly = setTimeout(updatePosition, 150);
    const settleLate = setTimeout(updatePosition, 350);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(settleEarly);
      clearTimeout(settleLate);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;

    const handleUpdate = () => updatePosition();

    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', handleUpdate);
      vv.addEventListener('scroll', handleUpdate);
    }

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
      if (vv) {
        vv.removeEventListener('resize', handleUpdate);
        vv.removeEventListener('scroll', handleUpdate);
      }
    };
  }, [isOpen, updatePosition]);

  return position;
}
